import { useEffect, useState } from 'react';
import { scopeFromLocation } from './scope';

const STORAGE_KEY = 'chopper.scope.v1';

function readCached() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.scope === 'string') return parsed;
    if (parsed && parsed.denied === true) return parsed;
    return null;
  } catch {
    return null;
  }
}

function writeCached(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // localStorage unavailable (private mode, etc.) — silently ignore.
  }
}

/**
 * Resolve a geohash3 personalization scope from the browser's geolocation API.
 * Returns null until a value is known. Persists both granted scopes and prior
 * denials in localStorage so we don't re-prompt on every page visit.
 *
 * @returns {string|null}
 */
export function useScope() {
  const [scope, setScope] = useState(() => {
    const cached = readCached();
    return cached && cached.scope ? cached.scope : null;
  });

  useEffect(() => {
    const cached = readCached();
    if (cached && (cached.scope || cached.denied)) return; // already resolved

    if (typeof navigator === 'undefined' || !navigator.geolocation) return;

    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled) return;
        const next = scopeFromLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        if (next) {
          writeCached({ scope: next, ts: Date.now() });
          setScope(next);
        }
      },
      () => {
        if (cancelled) return;
        writeCached({ denied: true, ts: Date.now() });
      },
      { enableHighAccuracy: false, maximumAge: 24 * 3600 * 1000, timeout: 8000 },
    );

    return () => { cancelled = true; };
  }, []);

  return scope;
}
